import React, { useState } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Button,
  Typography,
  Card,
  CardContent,
  Box,
} from "@material-ui/core";
import intl from "react-intl-universal";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useGet, useMutate } from "restful-react";
import { useSnackbar } from "notistack";
import { withRouter } from "react-router-dom";

import { TextField } from "components/TextField";
import { startLoadingAction, stopLoadingAction } from "components/ReduxSpinner";
import {
  getTeam,
  getUserRoles,
  patchTeam,
  IPatchTeamInput,
  ITeamsResponse,
  IPlayerOperationResponse,
  invitePlayerToTeam,
  removePlayerFromTeam,
} from "apis";
import { ErrorCode } from "utils/response";

import {
  FIELD_NAME,
  FIELD_ADDRESS,
  FIELD_DESCRIPTION,
  FIELD_NAME_LENGTH,
  FIELD_DESCRIPTION_LENGTH,
  FIELD_ADDRESS_LENGTH,
} from "./constants";
import { IEditTeamCompProps } from "./types";
import { PlayerList } from "./PlayerList";
import { InvitePlayerDialog } from "./InvitePlayerDialog";

const EditTeamComp = ({
  startLoading,
  stopLoading,
  match: {
    params: { teamId },
  },
}: IEditTeamCompProps) => {
  const [showDialog, setShowDialog] = useState(false);
  const { data: queryTeamResponse, refetch: refetchTeam } = useGet({
    path: getTeam(parseInt(teamId)),
  });

  const { data: userRoleResponse } = useGet({
    path: getUserRoles(parseInt(teamId)),
  });

  const viewerTeamRoles = userRoleResponse ? userRoleResponse.roles : [];

  const team = queryTeamResponse && queryTeamResponse.teams[0];

  const { mutate: mutatePatchTeam } = useMutate({
    verb: "PATCH",
    path: patchTeam(team ? team.id : -1),
  });

  const { mutate: mutateAddPlayers } = useMutate({
    verb: "POST",
    path: invitePlayerToTeam(team ? team.id : -1),
  });

  const { mutate: mutateRemovePlayer } = useMutate({
    verb: "DELETE",
    path: removePlayerFromTeam(team ? team.id : -1),
  });

  const { enqueueSnackbar } = useSnackbar();

  const showInviteDialog = () => {
    setShowDialog(true);
  };

  const handleHideDialog = () => {
    setShowDialog(false);
  };

  const handleUpdate = () => {
    refetchTeam();
  };

  const handleAddPlayers = (players: string) => {
    mutateAddPlayers({
      id: team.id,
      invitingAccounts: players,
    })
      .then((response: IPlayerOperationResponse) => {
        if (response.errCode < 0) {
          const msg =
            response.teamInviteFailedAccount.length > 0
              ? `${intl.get("TEAM_ADD_PLAYER_ERROR")} ${
                  response.teamInviteFailedAccount
                }`
              : intl.get("TEAM_ADD_PLAYER_SUCCESS");
          refetchTeam();
          setShowDialog(false);
          return enqueueSnackbar(msg, {
            variant:
              response.teamInviteFailedAccount.length > 0
                ? "warning"
                : "success",
          });
        }
        enqueueSnackbar(intl.get("TEAM_ADD_PLAYER_ERROR"), {
          variant: "error",
        });
      })
      .catch(() => {
        enqueueSnackbar(intl.get("TEAM_ADD_PLAYER_ERROR"), {
          variant: "error",
        });
      });
  };

  const handleRemove = (userId: number) => {
    mutateRemovePlayer(userId).then((response) => {
      if (response.errCode > 0) {
        return enqueueSnackbar(
          intl.get(
            response.errCode === ErrorCode.ERR_CANNOT_REMOVE_OWNER
              ? "TEAM_REMOVE_PLAYER_IS_OWNER"
              : "TEAM_REMOVE_PLAYER_ERROR"
          ),
          {
            variant:
              response.errCode === ErrorCode.ERR_CANNOT_REMOVE_OWNER
                ? "warning"
                : "error",
          }
        );
      }
      enqueueSnackbar(intl.get("TEAM_REMOVE_PLAYER_SUCCESS"), {
        variant: "success",
      });
      refetchTeam();
    });
  };

  if (!team) {
    return null;
  }

  const initialValues = {
    [FIELD_NAME]: team ? team.name : "",
    [FIELD_ADDRESS]: team ? team.address : "",
    [FIELD_DESCRIPTION]: team ? team.description : "",
  };

  return (
    <>
      <Card>
        <CardContent>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={async (values: any) => {
              startLoading();

              const input: IPatchTeamInput = {
                id: team.id,
                name: values[FIELD_NAME].trim(),
                address: values[FIELD_ADDRESS].trim(),
                description: values[FIELD_DESCRIPTION].trim(),
              };
              mutatePatchTeam(input)
                .then((response: ITeamsResponse) => {
                  if (response.errCode > 0) {
                    enqueueSnackbar(response.errMessage, {
                      variant: "error",
                    });
                    return;
                  }
                  enqueueSnackbar(intl.get("TEAM_UPDATE_SUCCESS"), {
                    variant: "success",
                  });
                })
                .catch(() => {
                  enqueueSnackbar(intl.get("TEAM_UPDATE_FAILED"), {
                    variant: "error",
                  });
                })
                .finally(() => stopLoading());
            }}
            validationSchema={Yup.object().shape({
              [FIELD_NAME]: Yup.string()
                .max(
                  FIELD_NAME_LENGTH,
                  intl.get("FORM_VALIDATION_LENGTH", {
                    length: FIELD_NAME_LENGTH,
                  })
                )
                .required(intl.get("FORM_VALIDATION_REQUIRED")),
              [FIELD_DESCRIPTION]: Yup.string().max(
                FIELD_DESCRIPTION_LENGTH,
                intl.get("FORM_VALIDATION_LENGTH", {
                  length: FIELD_DESCRIPTION_LENGTH,
                })
              ),
              [FIELD_ADDRESS]: Yup.string().max(
                FIELD_ADDRESS_LENGTH,
                intl.get("FORM_VALIDATION_LENGTH", {
                  length: FIELD_ADDRESS_LENGTH,
                })
              ),
            })}
          >
            {() => {
              return (
                <Form>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <Typography>
                        <strong>{`${intl.get("TEAM_EDIT_NAME")} *`}</strong>
                      </Typography>
                      <Field component={TextField} name={FIELD_NAME} />
                      <Box mt={2} />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom>
                        <strong>{intl.get("TEAM_EDIT_LOCATION")}</strong>
                      </Typography>
                      <Field component={TextField} name={FIELD_ADDRESS} />
                      <Box mt={2} />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom>
                        <strong>{intl.get("TEAM_EDIT_DESCRIPTION")}</strong>
                      </Typography>
                      <Field
                        component={TextField}
                        multiline
                        rows="4"
                        name={FIELD_DESCRIPTION}
                      />
                      <Box mt={2} />
                    </Grid>
                  </Grid>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    {intl.get("TEAM_UPDATE")}
                  </Button>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>
      <Box mt={4} />
      {team && (
        <PlayerList
          teamId={parseInt(teamId)}
          players={team.users}
          viewerRoles={viewerTeamRoles}
          onAdd={showInviteDialog}
          onRemove={handleRemove}
          onUpdate={handleUpdate}
        />
      )}

      {showDialog && (
        <InvitePlayerDialog
          onNewPlayerAdded={handleAddPlayers}
          hideDialog={handleHideDialog}
        />
      )}
    </>
  );
};

const mapDispatch = {
  startLoading: () => startLoadingAction,
  stopLoading: () => stopLoadingAction,
};

export const EditTeam = withRouter(connect(null, mapDispatch)(EditTeamComp));
